import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  X, ChevronLeft, ChevronRight, Heart, MessageCircle,
  Bookmark, Share2, Lock, Flame, Eye, Send, Info
} from "lucide-react";
import {
  doc, updateDoc, increment, collection,
  addDoc, getDocs, query, orderBy, serverTimestamp,
  setDoc, deleteDoc, getDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import SafeImage from "./SafeImage";
import { formatTimeAgo } from "../utils/formatTimeAgo";
import { getCreatorProfilePathFromHandle, getDropPath } from "../utils/profilePaths";
import EmptyCommentLine from "./EmptyCommentLine";

export default function MomentLightbox({ moments, initialIndex = 0, onClose }) {
  const { currentUser, userProfile } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [panelOpen, setPanelOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [showComments, setShowComments] = useState(false);

  // Touch gesture state
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchStartTime = useRef(null);
  const isDraggingPanel = useRef(false);

  const moment = moments[currentIndex];

  const isLimited = moment?.access_type === "limited" || moment?.accessType === "limited";
  const freeViewLimit = moment?.freeViewLimit || moment?.free_view_limit || 100;
  const viewCount = moment?.viewCount || moment?.view_count || 0;
  const viewsLeft = Math.max(freeViewLimit - viewCount, 0);
  const isLocked = isLimited && viewsLeft <= 0;
  const isAlmostGone = isLimited && viewsLeft > 0 && viewsLeft <= 15;
  const viewPercent = Math.min(100, (viewsLeft / freeViewLimit) * 100);

  const progressColor =
    viewPercent > 60 ? "#22c55e"
    : viewPercent > 30 ? "#f59e0b"
    : viewPercent > 10 ? "#f97316"
    : "#ef4444";

  // Reset when moment changes
  useEffect(() => {
    if (!moment) return;
    setLiked(false);
    setSaved(false);
    setLikeCount(moment.likesCount || moment.likes || 0);
    setComments([]);
    setShowComments(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Check persisted like/save state whenever the moment changes
  useEffect(() => {
    if (!currentUser || !moment?.id) return;

    const likeId = `${currentUser.uid}_${moment.id}`;
    const saveId = `${currentUser.uid}_${moment.id}`;

    getDoc(doc(db, "likes", likeId)).then((snap) => {
      if (snap.exists()) setLiked(true);
    }).catch(() => {});

    getDoc(doc(db, "saves", saveId)).then((snap) => {
      if (snap.exists()) setSaved(true);
    }).catch(() => {});
  }, [currentUser, moment?.id]);

  // Load comments
  useEffect(() => {
    if (!showComments || !moment?.id) return;
    const load = async () => {
      setCommentsLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, "moments", moment.id, "comments"), orderBy("createdAt", "asc"))
        );
        setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn("Comments load error:", e);
      } finally {
        setCommentsLoading(false);
      }
    };
    load();
  }, [showComments, moment?.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") { if (panelOpen) setPanelOpen(false); else onClose(); }
      if (e.key === "ArrowLeft") goTo(currentIndex - 1);
      if (e.key === "ArrowRight") goTo(currentIndex + 1);
      if (e.key === "i") setPanelOpen(prev => !prev);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, panelOpen]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const goTo = useCallback((index) => {
    if (index < 0 || index >= moments.length) return;
    setCurrentIndex(index);
    setPanelOpen(false);
  }, [moments.length]);

  // ── TOUCH HANDLERS ──
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isDraggingPanel.current = false;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;

    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const dt = Date.now() - touchStartTime.current;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Must be a reasonably fast swipe
    if (dt > 500) return;

    // Horizontal swipe — navigate photos (needs to be clearly horizontal)
    if (absDx > 50 && absDx > absDy * 1.5) {
      if (dx < 0) goTo(currentIndex + 1); // swipe left → next
      else goTo(currentIndex - 1);         // swipe right → prev
      return;
    }

    // Vertical swipe — panel control (needs to be clearly vertical)
    if (absDy > 50 && absDy > absDx * 1.5) {
      if (dy < 0) {
        // swipe up → open panel
        setPanelOpen(true);
        setShowComments(true);
      } else {
        // swipe down → close panel
        setPanelOpen(false);
      }
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentUser || likeLoading || !moment?.id) return;

    const likeId = `${currentUser.uid}_${moment.id}`;
    const likeRef = doc(db, "likes", likeId);
    const momentRef = doc(db, "moments", moment.id);
    const newLiked = !liked;

    setLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : Math.max(0, prev - 1));
    setLikeLoading(true);

    try {
      if (newLiked) {
        await setDoc(likeRef, {
          userId: currentUser.uid,
          postId: moment.id,
          creatorId: moment.creatorId || null,
          createdAt: serverTimestamp()
        });
        await updateDoc(momentRef, { likesCount: increment(1) });
      } else {
        await deleteDoc(likeRef);
        await updateDoc(momentRef, { likesCount: increment(-1) });
      }
    } catch {
      setLiked(!newLiked);
      setLikeCount(prev => newLiked ? prev - 1 : prev + 1);
    } finally {
      setLikeLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!currentUser || saveLoading || !moment?.id) return;

    const saveId = `${currentUser.uid}_${moment.id}`;
    const saveRef = doc(db, "saves", saveId);
    const newSaved = !saved;

    setSaved(newSaved);
    setSaveLoading(true);

    try {
      if (newSaved) {
        await setDoc(saveRef, {
          userId: currentUser.uid,
          postId: moment.id,
          creatorId: moment.creatorId || null,
          creatorUsername: moment.creatorUsername || moment.username || "",
          caption: moment.caption || "",
          mediaUrl: moment.mediaUrl || moment.media_url || "",
          savedAt: serverTimestamp()
        });
      } else {
        await deleteDoc(saveRef);
      }
    } catch {
      setSaved(!newSaved);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const path = getDropPath(moment.id);
    navigator.clipboard?.writeText(`${window.location.origin}${path || ""}`);
    setShareToast(true);
    setTimeout(() => setShareToast(false), 2000);
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!currentUser || !comment.trim()) return;
    const text = comment.trim();
    setComment("");
    const newComment = {
      id: Date.now().toString(),
      text,
      userId: currentUser.uid,
      displayName: userProfile?.displayName || "User",
      avatar: userProfile?.avatar || "",
      createdAt: { seconds: Date.now() / 1000 }
    };
    setComments(prev => [...prev, newComment]);
    try {
      await addDoc(collection(db, "moments", moment.id, "comments"), {
        text,
        userId: currentUser.uid,
        displayName: userProfile?.displayName || "User",
        avatar: userProfile?.avatar || "",
        createdAt: serverTimestamp()
      });
    } catch (e) {
      console.warn("Comment error:", e);
    }
  };

  if (!moment) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < moments.length - 1;

  return (
    <div
      className="lb-overlay"
      onClick={!panelOpen ? onClose : undefined}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >

      {/* Toast */}
      {shareToast && <div className="lb-toast">Link copied!</div>}

      {/* Top bar */}
      <div className="lb-topbar">
        <button className="lb-close" onClick={onClose}>
          <X size={18} strokeWidth={2.5} />
        </button>

        {moments.length > 1 && (
          <span className="lb-counter">{currentIndex + 1} / {moments.length}</span>
        )}

        <button
          className={`lb-info-btn ${panelOpen ? "lb-info-active" : ""}`}
          onClick={(e) => { e.stopPropagation(); setPanelOpen(prev => !prev); }}
          title="Show details (I)"
        >
          <Info size={18} strokeWidth={2.2} />
        </button>
      </div>

      {/* Main wrapper */}
      <div
        className={`lb-main ${panelOpen ? "lb-panel-open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Image area */}
        <div className="lb-image-area">

          {/* Prev — desktop only */}
          {hasPrev && (
            <button
              className="lb-nav lb-prev"
              onClick={(e) => { e.stopPropagation(); goTo(currentIndex - 1); }}
            >
              <ChevronLeft size={22} strokeWidth={2.5} />
            </button>
          )}

          {/* Photo */}
          <div className="lb-photo-wrap">
            <SafeImage
              src={moment.mediaUrl || moment.media_url}
              alt={moment.caption || "Moment"}
              className={`lb-photo ${isLocked ? "blurred-image" : ""}`}
              fallbackText=""
            />

            {/* View badge */}
            {isLimited && !isLocked && (
              <div className={`lb-view-badge ${isAlmostGone ? "lb-badge-urgent" : ""}`}>
                <Eye size={11} strokeWidth={2.5} />
                <div>
                  <strong>{viewsLeft} / {freeViewLimit}</strong>
                  <span>views left</span>
                </div>
              </div>
            )}

            {/* Almost gone */}
            {isAlmostGone && !isLocked && (
              <div className="lb-almost-gone">
                <Flame size={11} strokeWidth={2.5} />
                Almost gone — {viewsLeft} left
              </div>
            )}

            {/* Locked overlay */}
            {isLocked && (
              <div className="lb-locked">
                <Lock size={28} strokeWidth={1.5} color="white" />
                <p>Free window ended</p>
                <small>Support {moment.creatorDisplayName || moment.displayName} to unlock</small>
                <Link
                  to={getCreatorProfilePathFromHandle(moment.creatorUsername || moment.username, moment.creatorId) || "/gallery"}
                  className="lb-unlock-btn"
                  onClick={onClose}
                >
                  View support options
                </Link>
              </div>
            )}

            {/* Progress bar */}
            {isLimited && !isLocked && (
              <div className="lb-progress">
                <div className="lb-progress-fill" style={{ width: `${viewPercent}%`, background: progressColor }} />
              </div>
            )}

            {/* Mobile swipe hint — only shows when panel is closed */}
            {!panelOpen && (
              <div className="lb-swipe-hint">
                <div className="lb-swipe-hint-bar" />
                <span>Swipe up for details</span>
              </div>
            )}
          </div>

          {/* Next — desktop only */}
          {hasNext && (
            <button
              className="lb-nav lb-next"
              onClick={(e) => { e.stopPropagation(); goTo(currentIndex + 1); }}
            >
              <ChevronRight size={22} strokeWidth={2.5} />
            </button>
          )}

          {/* Bottom action bar */}
          <div className="lb-action-bar">
            <div className="lb-action-left">
              <button
                type="button"
                className={`lb-action-btn ${liked ? "lb-liked" : ""}`}
                onClick={handleLike}
                disabled={likeLoading}
              >
                <Heart size={19} strokeWidth={2} fill={liked ? "#fb7185" : "none"} />
                <span>{likeCount}</span>
              </button>

              <button
                type="button"
                className={`lb-action-btn ${showComments && panelOpen ? "lb-active" : ""}`}
                onClick={() => {
                  setShowComments(true);
                  setPanelOpen(true);
                }}
              >
                <MessageCircle size={19} strokeWidth={2} />
                <span>{moment.commentsCount || comments.length || 0}</span>
              </button>

              <button
                type="button"
                className={`lb-action-btn ${saved ? "lb-saved" : ""}`}
                onClick={handleSave}
                disabled={saveLoading}
              >
                <Bookmark size={19} strokeWidth={2} fill={saved ? "#7c3aed" : "none"} />
              </button>

              <button type="button" className="lb-action-btn" onClick={handleShare}>
                <Share2 size={19} strokeWidth={2} />
              </button>
            </div>

            <button
              type="button"
              className={`lb-panel-toggle ${panelOpen ? "lb-panel-toggle-open" : ""}`}
              onClick={() => setPanelOpen(prev => !prev)}
            >
              <Info size={15} strokeWidth={2.2} />
              {panelOpen ? "Hide details" : "Show details"}
            </button>
          </div>

          {/* Dot navigation */}
          {moments.length > 1 && (
            <div className="lb-dots">
              {moments.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`lb-dot ${i === currentIndex ? "lb-dot-active" : ""}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Slide-in panel */}
        <div className={`lb-panel ${panelOpen ? "lb-panel-visible" : ""}`}>

          {/* Drag handle */}
          <div className="lb-panel-handle" onClick={() => setPanelOpen(false)}>
            <div className="lb-panel-handle-bar" />
          </div>

          {/* Creator */}
          <div className="lb-panel-creator">
            <Link
              to={getCreatorProfilePathFromHandle(moment.creatorUsername || moment.username, moment.creatorId) || "/gallery"}
              className="lb-panel-creator-link"
              onClick={onClose}
            >
              <img
                src={moment.creatorAvatar || moment.profileImage || "https://i.pravatar.cc/100?img=5"}
                alt="Creator"
                className="lb-panel-avatar"
              />
              <div>
                <strong>{moment.creatorDisplayName || moment.displayName}</strong>
                <span>@{moment.creatorUsername || moment.username}</span>
              </div>
            </Link>
            <span className="lb-panel-time">{formatTimeAgo(moment.createdAt)}</span>
          </div>

          {/* Caption */}
          <div className="lb-panel-caption">
            {isLimited && viewsLeft > 0 && (
              <div className={`lb-status-pill ${isAlmostGone ? "pill-urgent" : "pill-free"}`}>
                {isAlmostGone ? "🔥 Almost gone" : "Free window open"}
              </div>
            )}
            {isLocked && <div className="lb-status-pill pill-locked">🔒 Locked</div>}
            <h3>{moment.caption || "Untitled moment"}</h3>
            {moment.description && <p>{moment.description}</p>}
            <Link to={getDropPath(moment.id) || "/gallery"} className="lb-view-full" onClick={onClose}>
              View full post →
            </Link>
          </div>

          {/* Comments */}
          <div className="lb-panel-comments">
            <div className="lb-comments-header">
              <strong>Comments</strong>
              <span>{moment.commentsCount || comments.length || 0}</span>
            </div>

            <div className="lb-comments-list">
              {commentsLoading ? (
                <p className="lb-comments-empty">Loading...</p>
              ) : comments.length === 0 ? (
                <EmptyCommentLine className="lb-comments-empty lb-comments-quiet" />
              ) : (
                comments.map((c) => (
                  <div key={c.id} className="lb-comment">
                    <img
                      src={c.avatar || "https://i.pravatar.cc/100?img=5"}
                      alt={c.displayName}
                      className="lb-comment-avatar"
                    />
                    <div className="lb-comment-body">
                      <strong>{c.displayName}</strong>
                      <p>{c.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {currentUser && (
              <form className="lb-comment-form" onSubmit={handleComment}>
                <img
                  src={userProfile?.avatar || "https://i.pravatar.cc/100?img=5"}
                  alt="You"
                  className="lb-comment-avatar"
                />
                <input
                  type="text"
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="lb-comment-input"
                />
                <button
                  type="submit"
                  className="lb-comment-submit"
                  disabled={!comment.trim()}
                >
                  <Send size={15} strokeWidth={2.5} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}